import { useCallback, useEffect, useState } from 'react'

/**
 * Minimal history router. Deep links work in both modes; no dependency needed for
 * the handful of routes this site has.
 */

function currentPath(): string {
  const p = window.location.pathname
  return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p
}

const listeners = new Set<() => void>()

function notify() {
  for (const l of listeners) l()
}

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (currentPath() === to) return
  if (opts.replace) window.history.replaceState(null, '', to)
  else window.history.pushState(null, '', to)
  notify()
}

export function useRoute(): { path: string; navigate: typeof navigate } {
  const [path, setPath] = useState(currentPath)

  useEffect(() => {
    const update = () => setPath(currentPath())
    listeners.add(update)
    window.addEventListener('popstate', update)
    return () => {
      listeners.delete(update)
      window.removeEventListener('popstate', update)
    }
  }, [])

  return { path, navigate }
}

/** Intercepts in-app anchor clicks so they route without a full reload. */
export function useLinkInterceptor(): void {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const anchor = (e.target as HTMLElement | null)?.closest('a')
      if (!anchor) return
      const href = anchor.getAttribute('href')
      if (!href || !href.startsWith('/') || anchor.target === '_blank') return
      e.preventDefault()
      navigate(href)
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [])
}

/**
 * On navigation, land on the scene the route names rather than the top of its chapter.
 * `anchorId` is the element id to scroll to, if the route resolves to one.
 */
export function useScrollReset(path: string, anchorId?: string): void {
  const reset = useCallback(() => {
    if (anchorId) {
      /* Wait for the chapter to paint before measuring. */
      requestAnimationFrame(() => {
        const el = document.getElementById(anchorId)
        if (el) {
          el.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'start' })
          return
        }
        window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
      })
      return
    }
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
  }, [anchorId])
  useEffect(reset, [path, reset])
}
